import {
  ReactElement,
  ReactNode,
  useState,
  useRef,
  MouseEvent,
  useEffect,
} from "react";
import styled from "styled-components";

import arrowDown from "../../assets/icons/arrow_down.png";
import { useOnClickOutside } from "../../hooks/UseOnClickOutside";
import { ErrorMessage } from "./ErrorMessage";
import { hexToRGB } from "../../utils/style";

/**
 * [共通パーツ]セレクト
 */

type SelectListProps = {
  dataId?: number;
  disabled?: boolean;
  value?: number | string;
  onValueChange: (value: number | string, dataId: string | null) => void;
  placeholder: string;
  items: Array<ReactElement<SelectListItemProps>>;
  error?: boolean;
  errorMessages?: string[];
  onCollapse?: () => void;
};

type SelectedItem = {
  value: string;
  children: ReactNode;
} | null;

const Container = styled.div`
  position: relative;
  border-radius: 6px;
`;

const Header = styled.div<{
  $disabled?: boolean;
  $isOpen: boolean;
  $error?: boolean;
}>`
  display: flex;
  background-color: ${(props) =>
    props.$error
      ? hexToRGB(props.theme.colors.caution, 0.05)
      : props.theme.colors.inputFieldBackground};
  cursor: pointer;
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  border: 1px solid
    ${(props) =>
      props.$error
        ? props.theme.colors.caution
        : props.$isOpen
          ? props.theme.colors.brand
          : props.theme.colors.primaryBorder};
  border-radius: 6px;
  ${(props) =>
    props.$isOpen && `border-bottom: none; border-radius: 6px 6px 0 0`};
  padding: 10px 0 10px 10px;
  min-height: 44px;
  ${(props) => props.$disabled && `opacity: 0.3`};
`;

const HeaderInner = styled.div<{ $isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${(props) => (props.$isSelected ? 1 : 0.7)};
  width: 100%;
`;

const ListContainer = styled.div`
  cursor: pointer;
  position: absolute;
  left: 0;
  background-color: ${(props) => props.theme.colors.primaryBackground};
  border: 1px solid ${(props) => props.theme.colors.brand};
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top: none;
  width: 100%;
  z-index: 1;
  div:last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  height: 150px;
  overflow: scroll;
`;

const Icon = styled.img`
  margin-right: 10px;
`;

const ItemWrapper = styled.div`
  padding: 10px;
  &:hover {
    background-color: #e1f3fd;
  }
`;

export function SelectList({
  dataId,
  placeholder,
  disabled = false,
  items,
  value,
  onValueChange,
  error,
  errorMessages,
  onCollapse,
}: SelectListProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedItem>(null);

  useEffect(() => {
    if (items.length > 0) {
      const selectedItem = items.find(
        (item: ReactElement<SelectListItemProps>) => item.props.value === value,
      );
      setSelected(selectedItem?.props || null);
    }
  }, [value, items]);

  useOnClickOutside(ref, () => setIsOpen(false));

  const toggleSelected = (
    item: ReactElement<SelectListItemProps>,
    event: MouseEvent,
  ) => {
    setIsOpen(!isOpen);
    setSelected(item.props);
    onValueChange(
      item.props.value,
      event.currentTarget.getAttribute("data-id"),
    );
  };

  const handleClick = () => {
    onCollapse && isOpen && onCollapse();
    setIsOpen(!isOpen);
  };

  return (
    <Container ref={ref}>
      <Header
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleClick}
        $error={error}
      >
        <HeaderInner $isSelected={!!selected}>
          {selected ? selected.children : placeholder}
          <Icon src={arrowDown} width="20" height="20" alt={"arrow_down"} />
        </HeaderInner>
      </Header>
      {isOpen && !disabled && (
        <ListContainer>
          {items.length > 0 &&
            items.map(
              (item: ReactElement<SelectListItemProps>, index: number) => (
                <ItemWrapper
                  data-id={dataId}
                  key={index}
                  onClick={(event) => toggleSelected(item, event)}
                >
                  {item.props.children}
                </ItemWrapper>
              ),
            )}
        </ListContainer>
      )}
      <ErrorMessage
        errorMessage={
          errorMessages && errorMessages.find((message) => message !== "")
        }
      />
    </Container>
  );
}

type SelectListItemProps = {
  value: string;
  children: ReactNode;
};

export function SelectListItem(props: SelectListItemProps) {
  return <div {...props}>{props.children}</div>;
}
